import React, { useEffect, useMemo, useState } from 'react';
import { professorAdminService, AdminProfessorSummary } from '../services/professorAdmin';
import { partnerService } from '../services/api';
import { Partner } from '../types';
import PasswordRequirements from '../components/PasswordRequirements';

type AdminProfessor = AdminProfessorSummary;

const stageOrder = ['profile_setup', 'course_info', 'syllabus_upload', 'approved', 'teaching_active'];

const ProfessorsManagement: React.FC = () => {
  const [professors, setProfessors] = useState<AdminProfessor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', password: '', host_partner_id: '' });
  const [creating, setCreating] = useState(false);
  const [hostPartners, setHostPartners] = useState<Partner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const hostPartnerLookup = useMemo(() => {
    const map = new Map<number, string>();
    hostPartners.forEach((partner) => map.set(partner.id, partner.name));
    return map;
  }, [hostPartners]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await professorAdminService.list();
      setProfessors(data.professors);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load professors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setPartnersLoading(true);
        const partners = await partnerService.getPartners();
        setHostPartners(partners.filter((p) => (p.partner_type || '').toUpperCase() === 'HOST_UNIVERSITY'));
      } catch (e) {
        setError('Failed to load host universities');
      } finally {
        setPartnersLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const handleSetStage = async (id: number, stage: string) => {
    try {
      setUpdatingId(id);
      await professorAdminService.setStage(id, stage);
      await load();
    } catch (e:any) {
      setError(e.response?.data?.error || 'Failed to update stage');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.first_name || !createForm.last_name || !createForm.email || !createForm.password || !createForm.host_partner_id) {
      setError('All fields including host university are required to create a professor');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      await professorAdminService.create({
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        email: createForm.email.trim(),
        password: createForm.password,
        host_partner_id: Number(createForm.host_partner_id)
      });
      setCreateForm({ first_name: '', last_name: '', email: '', password: '', host_partner_id: '' });
      await load();
    } catch (e:any) {
      setError(e.response?.data?.error || 'Failed to create professor');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="space-y-4">Loading professors...</div>;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded">{error}</div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Professors</h1>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Manage professor accounts and onboarding stages.</p>
        </div>
        <button onClick={load} className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-1 text-sm">Refresh</button>
      </div>
      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Add professor</h2>
        </div>
        <form onSubmit={handleCreateSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              value={createForm.first_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="First name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              type="text"
              value={createForm.last_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Last name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="prof@example.edu"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
            <input
              type="text"
              value={createForm.password}
              onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Set a starter password"
            />
            <PasswordRequirements
              className="mt-2"
              password={createForm.password}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Host University</label>
            <select
              value={createForm.host_partner_id}
              onChange={(e) => setCreateForm(prev => ({ ...prev, host_partner_id: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2 bg-white"
              disabled={partnersLoading || hostPartners.length === 0}
            >
              <option value="">Select host university</option>
              {hostPartners.map((partner) => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
            {partnersLoading && <p className="text-xs text-gray-500 mt-1">Loading host universities...</p>}
            {!partnersLoading && hostPartners.length === 0 && (
              <p className="text-xs text-red-500 mt-1">No host universities available. Create one first.</p>
            )}
          </div>
          <div className="md:col-span-4">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2 disabled:opacity-60"
            >
              {creating ? 'Creating...' : 'Create Professor'}
            </button>
          </div>
        </form>
      </div>
      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">All professors</h2>
          <p className="text-xs text-gray-500">{professors.length} total</p>
        </div>
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
          <thead className="bg-gray-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Professor</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Email</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Host University</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Current Stage</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-slate-200 uppercase tracking-wider">Set Stage</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:divide-slate-700 text-sm">
            {professors.map(p => {
              const displayName = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.username || p.email;
              return (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/60">
                  <td className="px-4 py-2 font-medium">{displayName}</td>
                  <td className="px-4 py-2">{p.email}</td>
                  <td className="px-4 py-2">{p.host_partner_id ? hostPartnerLookup.get(p.host_partner_id) || '—' : '—'}</td>
                  <td className="px-4 py-2">{p.current_stage || <span className="text-gray-400">(none)</span>}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {stageOrder.map(stage => {
                        const active = stage === p.current_stage;
                        return (
                          <button
                            key={stage}
                            disabled={active || updatingId === p.id}
                            onClick={() => handleSetStage(p.id, stage)}
                            className={`px-2 py-1 rounded text-xs border ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'} disabled:opacity-50`}
                          >
                            {stage.replace('_',' ')}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default ProfessorsManagement;
